import React, { useEffect, useState } from 'react';
import { FormControl, Skeleton, styled, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { DocumentReferencesView } from 'api';
import {
  MAX_DOCUMENT_SIZE_IN_MB,
  SUBTITLE,
  SUBTITLE_FOR_DROPPED_FILES,
  SUBTITLES_FOR_DIALOGS,
  TITLE,
  TITLE_ARRAY,
} from 'common/constants/documents';
import { TitleArray } from 'components/CircleIconWithText/types';
import { FormDialog } from 'components/Dialogs';
import validFileTypes from 'components/Documents/validFileTypes';
import DropZone from 'components/DropZone';
import { FileSrcProps } from 'components/DropZone/DropZone.props';
import useDocumentsContext from 'context/DocumentsContext';
import { useDocuments } from 'services/hooks';
import { FileAlerts } from '../alerts';
import { NotReferencedDocumentsList } from '../lists';
import { DividerWithTextProps, DocumentDialogProps } from './DocumentDialog.props';

const Container = styled('div')({
  display: 'flex',
  alignItems: 'center',
  margin: '1rem 0',
});

const Border = styled('div')({
  borderBottom: '1px solid lightgray',
  width: '100%',
});

const Content = styled('span')(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  fontWeight: 400,
  fontSize: 16,
  color: 'gray',
}));

const SelectorContainer = styled('div')({
  display: 'flex',
});

const SelectorLabel = styled('div')({
  margin: '0 0 1rem 1rem',
  flexShrink: 0,
});

const Select = styled('div')({
  width: '100%',
});

const DividerWithText = ({ children }: DividerWithTextProps) => (
  <Container>
    <Border />
    <Content>{children}</Content>
    <Border />
  </Container>
);

const DocumentDialog = ({
  open,
  onClose,
  addSelectComponent,
  title,
  droppedFiles,
  setDroppedFiles,
  selectedMeasurementDateId,
  dropZoneTitle,
  selectorTitle,
  referencedDocuments,
  customButtonLabel,
  referenceType,
  referencedFeatureId,
  fromReferenceWidget,
  isUploadEnabled = true,
  autoClassify = false,
}: DocumentDialogProps) => {
  const [selectedValues, setSelectedValues] = useState<DocumentReferencesView[]>();
  const { addReferenceForExistingDocuments } = useDocuments();
  const { updateReferencedDocuments, currentPage, documents } = useDocumentsContext();
  const fromDocumentFeature = true;

  const clearSelector = (): void => {
    setSelectedValues([]);
  };

  const handleActionButton = async (): Promise<void> => {
    if (selectedValues?.length) {
      await addReferenceForExistingDocuments(
        selectedMeasurementDateId,
        selectedValues,
        referencedFeatureId,
        referenceType
      );
      if (updateReferencedDocuments) {
        updateReferencedDocuments(documents, currentPage);
      }
    }
    onClose();
    setDroppedFiles([]);
  };

  const processFile = async ({ files }: FileSrcProps) => {
    if (files) {
      const fileList = [...files];
      setDroppedFiles(prevState => [...prevState, ...fileList]);
      if (fromReferenceWidget) {
        onClose();
      }
    }
  };

  useEffect(() => {
    clearSelector();
  }, [droppedFiles]);

  return (
    <FormDialog
      open={open}
      title={title}
      customButtonLabel={customButtonLabel}
      isValid={!isEmpty(droppedFiles) || (selectedValues?.length ?? 0) > 0}
      onSave={handleActionButton}
      onClose={onClose}
      bigDialog
      PaperProps={{ style: { width: 787 } }}>
      <div>
        {addSelectComponent && (
          <div>
            {selectorTitle && (
              <Typography variant="h5" gutterBottom>
                {selectorTitle}
              </Typography>
            )}
            {documents ? (
              <FormControl fullWidth>
                <SelectorContainer>
                  <NotReferencedDocumentsList
                    documents={documents}
                    setSelectedValues={setSelectedValues}
                    placeholder="Select a document"
                    referencedDocuments={referencedDocuments}
                  />
                </SelectorContainer>
                <DividerWithText>OR</DividerWithText>
              </FormControl>
            ) : (
              <Skeleton height={50} />
            )}
          </div>
        )}
        {dropZoneTitle && (
          <Typography variant="h5" gutterBottom>
            {dropZoneTitle}
          </Typography>
        )}
        <FormControl fullWidth>
          <div style={{ marginBottom: '1rem' }}>
            <DropZone
              setFileSrc={processFile}
              title={TITLE}
              titleArray={TITLE_ARRAY as TitleArray}
              subTitle={SUBTITLE}
              maxSizeAllowedInMb={MAX_DOCUMENT_SIZE_IN_MB}
              validFileTypes={validFileTypes}
              fromDocumentFeature={fromDocumentFeature}
              allowMultipleFiles
            />
            <Typography variant="caption" style={{ margin: '0.5rem 0', color: 'darkgray' }}>
              {SUBTITLES_FOR_DIALOGS('document')}
            </Typography>
          </div>
          {Array.isArray(droppedFiles) && droppedFiles.length === 0 && (
            <Typography variant="caption" style={{ margin: '0.5rem 0', color: 'darkgray' }}>
              {SUBTITLE_FOR_DROPPED_FILES}
            </Typography>
          )}
          {!isEmpty(droppedFiles) && isUploadEnabled && (
            <FileAlerts
              droppedFiles={droppedFiles}
              selectedMeasurementDateId={selectedMeasurementDateId}
              validFileTypes={validFileTypes}
              fromReferenceWidget={fromReferenceWidget}
              autoClassify={autoClassify}
            />
          )}
        </FormControl>
      </div>
    </FormDialog>
  );
};

export default DocumentDialog;
